import * as React from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Snackbar,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SendIcon from "@mui/icons-material/Send";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Cancel";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {alpha} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import WorkEvidenceTableComponent, {validateRecord} from "./WorkEvidenceTableComponent";
import {useWorkEvidence} from "../../hooks/workEvidence";
import {flatOuterCardSx, flatOuterCardContentSx} from "../../utils/mobileCardSx";
import {MobilePeriodBranchFilterBar} from "../Dashboard/MobilePeriodBranchFilterBar";

const DEFAULT_BOTTOM_BAR_CLEARANCE = 96;

// ─── Stat card ────────────────────────────────────────────────────────────────

interface StatCardProps {
    icon: React.ReactNode;
    value: number;
    label: string;
    iconBgColor: string | ((theme: import("@mui/material/styles").Theme) => string);
    iconColor: string;
}

function StatCard({icon, value, label, iconBgColor, iconColor}: StatCardProps): React.ReactElement {
    return (
        <Card variant="outlined" sx={{minWidth: 0, overflow: "hidden"}}>
            <CardContent
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: {xs: 1, sm: 1.5},
                    minWidth: 0,
                    p: {xs: 1.25, sm: 2},
                    "&:last-child": {pb: {xs: 1.25, sm: 2}},
                }}
            >
                <Box
                    sx={(theme) => ({
                        width: {xs: 32, sm: 44},
                        height: {xs: 32, sm: 44},
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: typeof iconBgColor === "function" ? iconBgColor(theme) : iconBgColor,
                        color: iconColor,
                        flexShrink: 0,
                    })}
                >
                    {icon}
                </Box>
                <Box sx={{minWidth: 0, overflow: "hidden"}}>
                    <Typography variant="h6" fontWeight={700} sx={{lineHeight: 1.2, fontSize: {xs: "1rem", sm: "1.25rem"}}}>
                        {value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{display: "block"}}>
                        {label}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function WorkEvidence(): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const {
        order,
        orderBy,
        handleRequestSort,
        selectedPeriod,
        selectedBranch,
        workEvidenceData,
        isLoadingData,
        tableDataError,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        editedCells,
        activeCellId,
        commentDrafts,
        handleCommentChange,
        successMessage,
        setSuccessMessage,
        isPeriodOpen,
        isPeriodLocked,
        handleCellValueChange,
        handleCellActivate,
        handleCellBlur,
        filteredData,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        stats,
        selected,
        isSelected,
        handleSelectAllClick,
        handleRowCheckboxClick,
        handleSubmitForApproval,
        handleSaveDraft,
        handleCancelEdits,
        uploading,
        uploadError,
        setUploadError,
        handleImportWorkEvidence,
        downloadWorkEvidence,
        downloading,
    } = useWorkEvidence();

    const hasEdits = Object.keys(editedCells).length > 0 || Object.keys(commentDrafts).length > 0;
    const submitCount = selected.length > 0 ? selected.length : workEvidenceData.length;

    // Mobile groups Save Draft + Submit for Approval into a sticky bottom bar (see mobile
    // reference layout) instead of splitting them between the header and the card body.
    const showMobileActionBar = isMobile && !!selectedPeriod;
    const canEditData = isPeriodOpen && workEvidenceData.length > 0;
    const [moreAnchor, setMoreAnchor] = React.useState<null | HTMLElement>(null);
    const bottomBarRef = React.useRef<HTMLDivElement | null>(null);
    const [bottomBarHeight, setBottomBarHeight] = React.useState(DEFAULT_BOTTOM_BAR_CLEARANCE);

    React.useLayoutEffect(() => {
        if (!showMobileActionBar || !bottomBarRef.current) return;
        const el = bottomBarRef.current;
        const updateHeight = () => setBottomBarHeight(el.offsetHeight);
        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, [showMobileActionBar]);

    const activeEmployeeId = selected.length === 1 ? selected[0] : null;
    const activeRecord = activeEmployeeId
        ? workEvidenceData.find((r) => r.employeeId === activeEmployeeId) ?? null
        : null;
    const activeValidation = activeRecord ? validateRecord(activeRecord, editedCells) : null;

    const handleFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleImportWorkEvidence(file);
        }
        event.target.value = "";
    };

    return (
        <Box sx={{pb: showMobileActionBar ? `${bottomBarHeight + 24}px` : 0}}>
            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                hidden
                onChange={handleFilePicked}
            />
            {isMobile && <MobilePeriodBranchFilterBar/>}

            {isPeriodLocked && (
                <Alert severity="warning" sx={{mt: 2}}>
                    {t('workEvidence.periodLockedWarning')}
                </Alert>
            )}

            {/* Stat cards */}
            {selectedPeriod && selectedBranch && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))"},
                        gap: {xs: 1, sm: 2},
                        mt: 3,
                    }}
                >
                    <StatCard
                        icon={<GroupsIcon/>}
                        value={stats.total}
                        label={t('workEvidence.stats.employees')}
                        iconBgColor={(theme) => alpha(theme.palette.primary.main, 0.12)}
                        iconColor="primary.main"
                    />
                    <StatCard
                        icon={<AccessTimeIcon/>}
                        value={stats.pending}
                        label={t('workEvidence.stats.pending')}
                        iconBgColor="warning.light"
                        iconColor="warning.dark"
                    />
                    <StatCard
                        icon={<SendIcon/>}
                        value={stats.submitted}
                        label={t('workEvidence.stats.submitted')}
                        iconBgColor={(theme) => alpha(theme.palette.info.main, 0.12)}
                        iconColor="info.main"
                    />
                    <StatCard
                        icon={<CheckCircleIcon/>}
                        value={stats.approved}
                        label={t('workEvidence.stats.approved')}
                        iconBgColor="success.light"
                        iconColor="success.dark"
                    />
                </Box>
            )}

            {uploadError && (
                <Alert severity="warning" sx={{mt: 2}} onClose={() => setUploadError(null)}>
                    {uploadError}
                </Alert>
            )}

            {/* Work Evidence Table */}
            <Card sx={isMobile ? {...flatOuterCardSx(true) as object, mt: 3} : {backgroundColor: "#fffcf8", border: "1px solid #ddd", borderRadius: 1, mt: 3}}>
                <CardContent sx={flatOuterCardContentSx(isMobile)}>
                    <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2}}>
                        <Typography variant="h6" sx={{textAlign: "left"}}>
                            {t('workEvidence.table.title')}
                        </Typography>
                        {!isMobile && (
                            <Box sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
                                <Button
                                    variant="outlined"
                                    startIcon={downloading ? <CircularProgress size={16}/> : <FileDownloadIcon/>}
                                    disabled={!selectedPeriod || downloading}
                                    onClick={downloadWorkEvidence}
                                >
                                    {downloading ? t('common.downloading') : t('common.actions.download')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={uploading ? <CircularProgress size={16}/> : <FileUploadIcon/>}
                                    disabled={!selectedPeriod || !selectedBranch || !isPeriodOpen || uploading}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {uploading ? t('workEvidence.import.uploading') : t('workEvidence.import.button')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SendIcon/>}
                                    disabled={!selectedPeriod || !selectedBranch || !isPeriodOpen || workEvidenceData.length === 0}
                                    onClick={handleSubmitForApproval}
                                >
                                    {t('workEvidence.submitForApproval')}{submitCount > 0 ? ` (${submitCount})` : ""}
                                </Button>
                            </Box>
                        )}
                    </Box>

                    {selectedPeriod && selectedBranch && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: {xs: "column", sm: "row"},
                                gap: 2,
                                alignItems: {xs: "stretch", sm: "center"},
                            }}
                        >
                            <TextField
                                size="small"
                                placeholder={t('workEvidence.search.placeholder')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{flex: 1, minWidth: {xs: "100%", sm: 260}}}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small"/>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip
                                    label={t('workEvidence.filterChip.all')}
                                    color={statusFilter === "ALL" ? "primary" : "default"}
                                    onClick={() => setStatusFilter("ALL")}
                                />
                                <Chip
                                    label={`${t('workEvidence.filterChip.pending')} (${stats.pending})`}
                                    color={statusFilter === "PENDING" ? "primary" : "default"}
                                    onClick={() => setStatusFilter("PENDING")}
                                />
                                <Chip
                                    label={`${t('workEvidence.filterChip.submitted')} (${stats.submitted})`}
                                    color={statusFilter === "SUBMITTED" ? "primary" : "default"}
                                    onClick={() => setStatusFilter("SUBMITTED")}
                                />
                                <Chip
                                    label={`${t('workEvidence.filterChip.approved')} (${stats.approved})`}
                                    color={statusFilter === "APPROVED" ? "primary" : "default"}
                                    onClick={() => setStatusFilter("APPROVED")}
                                />
                            </Stack>
                        </Box>
                    )}

                    <WorkEvidenceTableComponent
                        selectedPeriod={selectedPeriod}
                        selectedBranch={selectedBranch}
                        isLoadingData={isLoadingData}
                        tableDataError={tableDataError}
                        workEvidenceData={workEvidenceData}
                        filteredData={filteredData}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        isPeriodOpen={isPeriodOpen}
                        editedCells={editedCells}
                        activeCellId={activeCellId}
                        order={order}
                        orderBy={orderBy}
                        handleRequestSort={handleRequestSort}
                        handleCellActivate={handleCellActivate}
                        handleCellBlur={handleCellBlur}
                        handleCellValueChange={handleCellValueChange}
                        selected={selected}
                        isSelected={isSelected}
                        onSelectAllClick={handleSelectAllClick}
                        onRowCheckboxClick={handleRowCheckboxClick}
                        onPageChange={setCurrentPage}
                        commentDrafts={commentDrafts}
                        onCommentChange={handleCommentChange}
                    />

                    {selectedPeriod && selectedBranch && workEvidenceData.length > 0 && (
                        <Alert severity="info" icon={false} sx={{mt: 2}}>
                            {t('workEvidence.info.hourRules')}
                        </Alert>
                    )}

                    {!isMobile && activeRecord && activeValidation && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: {xs: "column", md: "row"},
                                gap: 2,
                                mt: 3,
                            }}
                        >
                            <Box sx={{flex: 1}}>
                                <Typography variant="subtitle2" sx={{textAlign: "left", mb: 1}}>
                                    {t('workEvidence.note.title', {
                                        firstName: activeRecord.firstName,
                                        lastName: activeRecord.lastName,
                                    })}
                                </Typography>
                                <TextField
                                    multiline
                                    minRows={3}
                                    fullWidth
                                    disabled={!isPeriodOpen}
                                    value={commentDrafts[activeRecord.employeeId] ?? activeRecord.comment ?? ""}
                                    onChange={(e) => handleCommentChange(activeRecord.employeeId, e.target.value.slice(0, 500))}
                                />
                                <Typography variant="caption" color="text.disabled" sx={{display: "block", textAlign: "right"}}>
                                    {(commentDrafts[activeRecord.employeeId] ?? activeRecord.comment ?? "").length} / 500
                                </Typography>
                            </Box>
                            <Box sx={{flex: 1}}>
                                <Typography variant="subtitle2" sx={{textAlign: "left", mb: 1}}>
                                    {t('workEvidence.validation.title')}
                                </Typography>
                                <Stack spacing={1}>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                        {activeValidation.hoursFilled ? (
                                            <CheckCircleIcon fontSize="small" color="success"/>
                                        ) : (
                                            <CancelIcon fontSize="small" color="disabled"/>
                                        )}
                                        <Typography variant="body2">{t('workEvidence.validation.hoursFilled')}</Typography>
                                    </Box>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                        {activeValidation.noNegativeValues ? (
                                            <CheckCircleIcon fontSize="small" color="success"/>
                                        ) : (
                                            <CancelIcon fontSize="small" color="error"/>
                                        )}
                                        <Typography variant="body2">{t('workEvidence.validation.noNegativeValues')}</Typography>
                                    </Box>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                        {hasEdits ? (
                                            <AccessTimeIcon fontSize="small" sx={{color: "warning.main"}}/>
                                        ) : (
                                            <CheckCircleIcon fontSize="small" color="success"/>
                                        )}
                                        <Typography variant="body2">
                                            {hasEdits ? t('workEvidence.validation.pendingSubmission') : t('workEvidence.validation.readyToSubmit')}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        </Box>
                    )}

                    {canEditData && (
                        <>
                            <Divider sx={{my: 3}}/>
                            <Box sx={{display: "flex", flexWrap: "wrap", gap: 2}}>
                                {!isMobile && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<SaveIcon/>}
                                        disabled={!hasEdits}
                                        onClick={handleSaveDraft}
                                    >
                                        {t('workEvidence.draft.save')}
                                    </Button>
                                )}
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    disabled={!hasEdits}
                                    onClick={handleCancelEdits}
                                >
                                    {t('common.actions.cancel')}
                                </Button>
                            </Box>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Success Snackbar */}
            <Snackbar
                open={successMessage !== null}
                autoHideDuration={3000}
                onClose={() => setSuccessMessage(null)}
                anchorOrigin={{vertical: "top", horizontal: "right"}}
            >
                <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{width: "100%"}}>
                    {successMessage}
                </Alert>
            </Snackbar>

            {/* Sticky mobile action bar - groups Save Draft + Submit for Approval together (see
                mobile reference layout), instead of splitting them across the header and card body. */}
            {showMobileActionBar && (
                <Box
                    ref={bottomBarRef}
                    sx={{
                        position: "fixed", left: 0, right: 0, bottom: 0, p: 1.5,
                        backgroundColor: "background.paper", borderTop: "1px solid", borderColor: "divider",
                        zIndex: (t2) => t2.zIndex.appBar,
                    }}
                >
                    {selected.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{display: "block", mb: 1, textAlign: "left"}}>
                            {t('workEvidence.mobile.selectedCount', {count: selected.length})}
                        </Typography>
                    )}
                    <Box sx={{display: "flex", gap: 1.5}}>
                        <Button
                            variant="outlined"
                            startIcon={<SaveIcon/>}
                            disabled={!hasEdits || !canEditData}
                            onClick={handleSaveDraft}
                            sx={{flex: 1}}
                        >
                            {t('workEvidence.draft.save')}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SendIcon/>}
                            disabled={!selectedPeriod || !selectedBranch || !canEditData}
                            onClick={handleSubmitForApproval}
                            sx={{flex: 1.4}}
                        >
                            {t('workEvidence.submitForApproval')}{submitCount > 0 ? ` (${submitCount})` : ""}
                        </Button>
                        <IconButton
                            sx={{border: "1px solid", borderColor: "divider", borderRadius: 1}}
                            onClick={(e) => setMoreAnchor(e.currentTarget)}
                            aria-label={t('payroll.run.mobile.moreActions')}
                        >
                            <MoreVertIcon/>
                        </IconButton>
                        <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)}>
                            <MenuItem
                                disabled={!selectedPeriod || downloading}
                                onClick={() => {
                                    setMoreAnchor(null);
                                    downloadWorkEvidence();
                                }}
                            >
                                {downloading ? <CircularProgress size={16} sx={{mr: 1.5}}/> : <FileDownloadIcon fontSize="small" sx={{mr: 1.5}}/>}
                                {downloading ? t('common.downloading') : t('common.actions.download')}
                            </MenuItem>
                            <MenuItem
                                disabled={!selectedPeriod || !selectedBranch || !isPeriodOpen || uploading}
                                onClick={() => {
                                    setMoreAnchor(null);
                                    fileInputRef.current?.click();
                                }}
                            >
                                {uploading ? <CircularProgress size={16} sx={{mr: 1.5}}/> : <FileUploadIcon fontSize="small" sx={{mr: 1.5}}/>}
                                {uploading ? t('workEvidence.import.uploading') : t('workEvidence.import.button')}
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
